import { PrmService } from '../gs-core/common';
import {
  Doc,
  SignedUpload,
  Subject, hydrateOrgs, shallowValidateFile, DocUpload, DocUploadSteps
} from '../common/models';
import { Organization, ValidationError } from '../gs-core/common/models';
import { DocService } from './DocService';
import { CONCURRENT_UPLOADS, DocUploadMap } from '../common/models/DocUploadMap';
import { PromiseQueue, timeoutPromise } from '../gs-core/common/util';

/**
 * This class mutates docUploads because there are thousands of events firing during file uploads
 * and avoiding unnecessary memory allocations and object creation will speed up the browser.
 */
export class DocUploadService {
  private static MAX_UPLOAD_RETRIES = 3;
  private prmService: PrmService; private docService: DocService;

  constructor(prmService: PrmService, docService: DocService) {
    this.prmService = prmService;
    this.docService = docService;
  }

  async doUploads(
    docUploadMap: DocUploadMap,
    onChangeMap: (docUploadMap: DocUploadMap) => void,
    subject?: Subject,
    owningUnit?: Organization,
  ) {
    const progressCallback = () => onChangeMap(docUploadMap);

    const pending = docUploadMap.byStep(DocUploadSteps.PENDING);

    // Make a copy so we can safely iterate while transitioning items out of this array
    // Reversed order so the first doc dragged in gets the largest createdDate
    const docsToUpload = [...pending].reverse();

    for (const docUpload of docsToUpload) {
      docUploadMap.transition(docUpload, DocUploadSteps.CHECKING_FILE);
      this.checkFile(docUpload);
    }

    onChangeMap(docUploadMap);

    const checkingErrors = docsToUpload.filter(docUpload => !!docUpload.error);

    if (checkingErrors.length) {
      throw new ValidationError(`Stopped upload because ${checkingErrors.length} files had problems`);
    }

    const uploadQueue = new PromiseQueue<void>(CONCURRENT_UPLOADS);
    const uploadPromises = [];

    // Sequential createDoc calls to preserve the order the files were dragged in (createdDate desc sorting)
    for (const docUpload of docsToUpload) {
      docUploadMap.transition(docUpload, DocUploadSteps.CREATE_DOC);
      onChangeMap(docUploadMap);
      await this.createDoc(docUpload, subject, owningUnit);
      onChangeMap(docUploadMap);

      if (!docUpload.error) {
        const uploadPromise = uploadQueue.add(async () => {
          docUploadMap.transition(docUpload, DocUploadSteps.UPLOADING);
          onChangeMap(docUploadMap);
          await this.uploadWithRetry(docUpload, progressCallback);
          onChangeMap(docUploadMap);

          if (!docUpload.error) {
            docUploadMap.transition(docUpload, DocUploadSteps.CLEANING);
            onChangeMap(docUploadMap);
            await this.clean(docUpload);
            if (!docUpload.error) {
              docUploadMap.transition(docUpload, DocUploadSteps.DONE);
            }
            onChangeMap(docUploadMap);
          }
        });

        uploadPromises.push(uploadPromise);
      }
    }

    await Promise.all(uploadPromises);
  }

  checkFile(docUpload: DocUpload) {
    try {
      shallowValidateFile(docUpload.file);
    } catch (err) {
      docUpload.error = err;
    }
  }

  async createDoc(
    docUpload: DocUpload,
    subject?: Subject,
    owningUnit?: Organization
  ): Promise<void> {
    try {
      const doc = await this.docService.create(docUpload.file);
      subject = subject || doc.subject;
      owningUnit = owningUnit || doc.owningUnit;
      docUpload.doc = await hydrateOrgs({ ...doc, subject, owningUnit }, this.prmService);
    } catch (err) {
      docUpload.error = err;
    }
  }

  async uploadWithRetry(docUpload: DocUpload, progressCallback: () => void): Promise<void> {
    let tries = 0;

    while (true) {
      try {
        return this.upload(docUpload, progressCallback);
      } catch (err) {
        tries++;

        if (tries >= DocUploadService.MAX_UPLOAD_RETRIES) {
          docUpload.error = err;
          return;
        }

        await timeoutPromise(tries * 100);
      }
    }
  }

  async upload(docUpload: DocUpload, progressCallback: () => void): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!docUpload.doc) {
        throw new Error('Missing doc to upload');
      }

      const request = new XMLHttpRequest();

      let loadEvent: () => void;
      let progressEvent: (event: ProgressEvent) => void;
      let errorEvent: (event: ErrorEvent) => void;

      const bindEvents = () => {
        request.addEventListener('load', loadEvent);
        request.upload.addEventListener('progress', progressEvent);
        request.addEventListener('error', errorEvent);
      };

      const unbindEvents = () => {
        request.removeEventListener('load', loadEvent);
        request.upload.removeEventListener('progress', progressEvent);
        request.removeEventListener('error', errorEvent);
      };

      loadEvent = () => {
        if (request.readyState === XMLHttpRequest.DONE) {
          unbindEvents();
          if (request.status >= 400) {
            return reject(new Error(request.responseText));
          }
          return resolve();
        }
      };

      progressEvent = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          docUpload.percentComplete = Math.floor(event.loaded / event.total * 100);
          progressCallback();
        }
      };

      errorEvent = (event: ErrorEvent) => {
        unbindEvents();
        reject(event.error);
      };

      bindEvents();

      const signedUpload = docUpload.doc.signedUpload as SignedUpload;

      request.open('POST', signedUpload.url);

      const body = Object.keys(signedUpload.fields)
        .reduce(
          (formData, key) => {
            formData.append(key, signedUpload.fields[key]);
            return formData;
          },
          new FormData()
        );

      body.append('file', docUpload.file);

      Object.keys(signedUpload.headers).forEach(key =>
        request.setRequestHeader(key, signedUpload.headers[key])
      );

      request.send(body);
    });
  }

  async clean(docUpload: DocUpload) {
    try {
      docUpload.doc = await this.docService.save(docUpload.doc as Doc);
    } catch (err) {
      docUpload.error = err;
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/DocUploadService.ts